import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SettingsIcon, GitMergeIcon, WorkflowIcon, DatabaseIcon, MessageSquareIcon, FileTextIcon, CheckCircleIcon, ArrowRightIcon, SearchIcon, MapIcon, WrenchIcon, ActivityIcon, RocketIcon, LayersIcon, CpuIcon, AlertCircleIcon, ChevronDownIcon, ZapIcon, BuildingIcon, UsersIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function CustomAutomationsPage() {
  const faqs = [{
    question: 'What kind of automations can you build for contractors?',
    answer: 'We build bespoke workflows to eliminate manual data entry. Common examples include syncing leads from ad platforms directly into your dispatch software, automatically sending estimate follow-ups, triggering review requests when a job is marked complete, and routing specific job types to specific crews.'
  }, {
    question: 'How long does a custom automation project take?',
    answer: 'Simple automations (like connecting a web form to a Google Sheet) can be done in a few days. Complex, multi-step workflows involving custom API integrations and conditional logic typically take 2-4 weeks to build, test, and deploy.'
  }, {
    question: 'What tools and platforms can you integrate?',
    answer: 'We can integrate with almost any software that has an open API. We frequently work with ServiceTitan, Jobber, Housecall Pro, GoHighLevel, Salesforce, HubSpot, QuickBooks, Stripe, Google Workspace, and major ad platforms.'
  }, {
    question: 'Do I need to know how to code?',
    answer: 'No. We handle all the technical architecture, coding, and API connections. We deliver a finished, working system and provide you with a simple dashboard to monitor it, along with training for your team.'
  }, {
    question: 'How much does a custom automation cost?',
    answer: 'Pricing depends entirely on the complexity of the workflow and the systems involved. We start with a free scoping call to understand your operational bottlenecks, then provide a flat-rate project fee.'
  }, {
    question: 'Can you automate workflows I already have in place?',
    answer: 'Yes. We often audit existing, manual workflows (like office staff copy-pasting data between spreadsheets) and build automated systems to replace them, saving hours of administrative work each week and eliminating human error.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-1687" data-mp="12una60cecevt">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1688" data-mp="12sudndvhzb1j">
          <span className="text-lg font-bold text-white group-hover:text-red-500 transition-colors" data-id="element-1689" data-mp="15ejvj5iap0gp">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-red-500' : ''}`} data-id="element-1690" data-mp="1f5680quptav6" />
        </button>
        <AnimatePresence data-id="element-1691" data-mp="mgh3ehuganeo">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-1692" data-mp="mhsi3cpfpfkk">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1693" data-mp="2bunjxnkajt5b">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="pt-24 pb-16" data-id="element-1694" data-mp="25f6p7h7k8rwi">
      <SEOHead title="Custom Automations & API Integrations | Viridia Analytics" description="Bespoke automation workflows for home service businesses. Connect your tools, eliminate manual data entry, and build systems tailored to your unique operations." canonical="/custom-automations" keywords="custom automations, API integrations, workflow automation, business process automation, Zapier alternative, custom webhooks, Phoenix automation agency, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-1695" data-mp="25g8byixxv2u2" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-1696" data-mp="2y4cif6oc3jxt">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1697" data-mp="3c2u3sxqrokck">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-red-500/10 blur-[120px] animate-pulse-slow" data-id="element-1698" data-mp="382tfmldv8usb"></div>
        </div>

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgNDBoNDBWMEgwem0zOS0zOXYzOEgxVjFoMzh6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] -z-10 opacity-50" data-id="element-1699" data-mp="3vpnhuk2d9i6j"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1700" data-mp="2z0fur7m3n10i">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1701" data-mp="32zycxvmjcxzn">
            <ScrollReveal data-id="element-1702" data-mp="2p0yuj5pm9fbx">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-medium mb-6" data-id="element-1703" data-mp="2p0p2lcl0gy3l">
                <SettingsIcon className="w-4 h-4" data-id="element-1704" data-mp="zjtej79bk074" />
                <span data-id="element-1705" data-mp="zkmpkkfb9bv2">Bespoke Systems Engineering</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-1706" data-mp="usuwo38omu1r">
                Connect Your Tools. <br data-id="element-1707" data-mp="qsaxyv8gnidi" />
                <span className="text-red-500" data-id="element-1708" data-mp="qpf2ddk355s8">Automate the Rest.</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl" data-id="element-1709" data-mp="lu9jknlzc78d">
                When off-the-shelf software doesn't fit your unique process, we
                build custom workflows that make your existing tools talk to
                each other. Eliminate manual data entry and operational
                bottlenecks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1710" data-mp="14j6biktwmnnf">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1711" data-mp="14l0oncyxpbgy">
                  Book a Scoping Call
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="hidden lg:block" data-id="element-1712" data-mp="3k1yp55hqn74s">
              <div className="relative" data-id="element-1713" data-mp="3p1th7cd922rf">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-1714" data-mp="3tu4hwhftdsl1">
                  {/* Workflow Diagram Visualization */}
                  <div className="flex flex-col items-center gap-4 relative z-10 py-4" data-id="element-1715" data-mp="1sennvqho3cs">
                    {/* Trigger */}
                    <motion.div initial={{
                    opacity: 0,
                    y: -20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.5
                  }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg" data-id="element-1716" data-mp="bohydpzepabc">
                      <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1717" data-mp="j77r05u00qny">
                        <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-1718" data-mp="xw3x3xchvtlx" />
                      </div>
                      <div className="min-w-0" data-id="element-1719" data-mp="sylwcu8mskp0">
                        <div className="text-xs text-gray-500" data-id="element-1720" data-mp="57pwd9u0uj6o">Trigger</div>
                        <div className="text-sm font-medium text-white truncate" data-id="element-1721" data-mp="94w5aerrzi8">
                          New Quote Form Submitted
                        </div>
                      </div>
                    </motion.div>

                    <div className="w-px h-8 bg-gray-700" data-id="element-1722" data-mp="a6t2kxlqmvg8"></div>

                    {/* Logic Branch */}
                    <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3
                  }} className="bg-viridia-black border border-viridia/30 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg relative" data-id="element-1723" data-mp="1pnbfsozwpld3">
                      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1724" data-mp="1o7fr1d0rjsqi"></div>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1725" data-mp="2bxa2bq5fp98n"></div>

                      <div className="w-8 h-8 rounded bg-viridia/20 flex items-center justify-center" data-id="element-1726" data-mp="2gxnyoaxoauqu">
                        <CpuIcon className="w-4 h-4 text-viridia" data-id="element-1727" data-mp="224ul4v1yooot" />
                      </div>
                      <div data-id="element-1728" data-mp="273p0to4mtx4p">
                        <div className="text-xs text-gray-500" data-id="element-1729" data-mp="2uvmcnf830p5t">Action</div>
                        <div className="text-sm font-medium text-white" data-id="element-1730" data-mp="2utje26sv5nph">
                          AI Summarizes Request
                        </div>
                      </div>
                    </motion.div>

                    <div className="flex w-full max-w-md justify-between px-12 relative" data-id="element-1731" data-mp="1z3qrdfo2sh2x">
                      <div className="w-px h-8 bg-gray-700 absolute left-1/4 top-0" data-id="element-1732" data-mp="1z1ecxchj0oum"></div>
                      <div className="w-px h-8 bg-gray-700 absolute right-1/4 top-0" data-id="element-1733" data-mp="1l627aa1u1dyx"></div>
                      <div className="w-1/2 h-px bg-gray-700 absolute left-1/4 top-0" data-id="element-1734" data-mp="1q17v5eesp2pn"></div>
                    </div>

                    {/* Outcomes */}
                    <div className="flex gap-4 sm:gap-8 w-full justify-center flex-wrap sm:flex-nowrap" data-id="element-1735" data-mp="1g54pvzoncnyh">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.6
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1736" data-mp="12ej6m9va6c02">
                        <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center" data-id="element-1737" data-mp="nrkhiljwbtoi">
                          <DatabaseIcon className="w-4 h-4 text-green-400" data-id="element-1738" data-mp="iq3hes98kb8z" />
                        </div>
                        <div data-id="element-1739" data-mp="wpwd71zmdhnt">
                          <div className="text-xs text-gray-500" data-id="element-1740" data-mp="spucow5meua4">CRM</div>
                          <div className="text-sm font-medium text-white" data-id="element-1741" data-mp="spm075detqzg">
                            Create Record
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: 20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.8
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1742" data-mp="11k8zdjshd739">
                        <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1743" data-mp="1xa9a6i0fqczk">
                          <MessageSquareIcon className="w-4 h-4 text-purple-400" data-id="element-1744" data-mp="268lc3g6r4x7b" />
                        </div>
                        <div className="min-w-0" data-id="element-1745" data-mp="2b7xzq35g68ay">
                          <div className="text-xs text-gray-500" data-id="element-1746" data-mp="2b64o09fz8xw3">Slack</div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-1747" data-mp="2g6sgkzjutmsf">
                            Alert Sales Team
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Animated Pulses */}
                    <motion.div animate={{
                    top: ['10%', '90%'],
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-viridia rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)] z-20" data-id="element-1748" data-mp="eajt5hmm91y7"></motion.div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Custom Automations? */}
      <section className="py-20 bg-viridia-black" data-id="element-1749" data-mp="iau478p9mijx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1750" data-mp="381747qrinqyx">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1751" data-mp="37ykzdyw0fpke">
            <ScrollReveal data-id="element-1752" data-mp="330t2eqgidd9c">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1753" data-mp="333p14h06k5yu">
                Off-the-Shelf Software Rarely Fits Perfectly
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1754" data-mp="10i5ryh2zq52g">
                Most local businesses use 5-10 different software tools to run
                their operations. When these tools don't talk to each other,
                your team ends up doing manual data entry, leads fall through
                the cracks, and scaling becomes impossible.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1755" data-mp="1jg8d2ksf3huz">
                We build the "glue" that connects your systems. Whether it's a
                simple Zapier integration or a complex custom webhook, we
                engineer workflows that match exactly how you do business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1756" data-mp="3p85dk8zb8lb2">
                <div className="bg-viridia-surface border border-gray-800 rounded-xl p-4 flex items-start gap-3" data-id="element-1757" data-mp="3u8jbuzb40gvw">
                  <AlertCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1758" data-mp="2c9bv9owova8o" />
                  <div data-id="element-1759" data-mp="27c3bxkzt105u">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1760" data-mp="27aijc0x1vram">
                      Without Automation
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1761" data-mp="2l8qkkl14derl">
                      Manual data entry, delayed follow-ups, human error.
                    </p>
                  </div>
                </div>
                <div className="bg-viridia-surface border border-viridia/30 rounded-xl p-4 flex items-start gap-3" data-id="element-1762" data-mp="1thj5e3cx2556">
                  <ZapIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-1763" data-mp="22byvfuywc5fm" />
                  <div data-id="element-1764" data-mp="1xlig25bim9qm">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1765" data-mp="1xkyw6j2b1b9y">
                      With Viridia
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1766" data-mp="19ulbjdm1uepe">
                      Instant data sync, automated tasks, scalable operations.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <div className="relative" data-id="element-1767" data-mp="39xw19j0y3rwe">
              <div className="absolute inset-0 bg-viridia/20 blur-[100px] rounded-full" data-id="element-1768" data-mp="39wuptdg0p3e5"></div>
              <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 shadow-2xl" data-id="element-1769" data-mp="3epxa0uywn0ho">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1770" data-mp="3jmnr9lmg424o">
                  <WrenchIcon className="w-5 h-5 text-viridia" data-id="element-1771" data-mp="2m1i89a4saeoa" /> Our Approach
                </h3>
                <ul className="space-y-6" data-id="element-1772" data-mp="2r02xkeihzdxy">
                  <li className="flex items-start gap-4" data-id="element-1773" data-mp="2r14kphcsfqw1">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1774" data-mp="2vxlaefzmydb8">
                      1
                    </div>
                    <div data-id="element-1775" data-mp="b7dqqhy7f4z0">
                      <h4 className="text-white font-bold mb-1" data-id="element-1776" data-mp="ba9ni2ybfnlv">
                        Process Audit
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1777" data-mp="20p163lwgnefy">
                        We map out your current manual process and identify the
                        bottlenecks costing you time.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1778" data-mp="1qxeliyy8g30j">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1779" data-mp="1lzd6cp3xgnuc">
                      2
                    </div>
                    <div data-id="element-1780" data-mp="1cyn9rj6uetfk">
                      <h4 className="text-white font-bold mb-1" data-id="element-1781" data-mp="1cyf00060o48l">
                        Architecture Design
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1782" data-mp="131sed95v995i">
                        We design a robust, scalable system architecture using
                        the right tools (APIs, Webhooks, Zapier, Make).
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1783" data-mp="2xdqr3l3dwn65">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1784" data-mp="26bili3ol7vuh">
                      3
                    </div>
                    <div data-id="element-1785" data-mp="1wjmkajr7fpm1">
                      <h4 className="text-white font-bold mb-1" data-id="element-1786" data-mp="1wibj8rp58vym">
                        Build & Test
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1787" data-mp="1njzjo06aunu1">
                        We build the automation, run extensive edge-case
                        testing, and ensure data integrity.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1788" data-mp="1dnmthc9yqs41">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1789" data-mp="1iloazsrqqjde">
                      4
                    </div>
                    <div data-id="element-1790" data-mp="3cxl7w18ychfu">
                      <h4 className="text-white font-bold mb-1" data-id="element-1791" data-mp="3cxuxvotzyolj">
                        Deployment & Monitoring
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1792" data-mp="wchl85rjqz9p">
                        We launch the system and provide ongoing monitoring to
                        ensure it runs flawlessly 24/7.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When Custom Makes Sense */}
      <section className="py-20 bg-viridia-surface" data-id="element-1793" data-mp="1g0qb295mat61">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1794" data-mp="1fzxeu24abm25">
          <ScrollReveal data-id="element-1795" data-mp="1tywzyzsl9itk">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1796" data-mp="1tz6sov50q42y">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1797" data-mp="1p0lo6jiqgy8p">
                When Do You Need Custom Automations?
              </h2>
              <p className="text-gray-400" data-id="element-1798" data-mp="3vrl7hlc9msf5">
                Our standard packages cover 90% of local business needs. Custom
                automations are for the 10% that makes your business unique.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1799" data-mp="1iavk516prgf0">
            <ScrollReveal data-id="element-1800" data-mp="1w1h5cwjn42g2">
              <div className="space-y-6" data-id="element-1801" data-mp="1w1qr27xnjnfy">
                <div className="flex items-start gap-4" data-id="element-1802" data-mp="1w3l9nflgb3gv">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1803" data-mp="1r3fehpfz2yu1">
                    <LayersIcon className="w-5 h-5 text-viridia" data-id="element-1804" data-mp="13dbl0awi0fgr" />
                  </div>
                  <div data-id="element-1805" data-mp="zcjalebw44iw">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1806" data-mp="zdays435qzz5">
                      Disconnected Software Stack
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1807" data-mp="18bmx6sa9n225">
                      Your field service software (like ServiceTitan or Jobber)
                      doesn't talk to your CRM or accounting software, forcing
                      you to enter data twice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1808" data-mp="5hqw6gbeqbop">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1809" data-mp="aiwq2hgt04zt">
                    <AlertCircleIcon className="w-5 h-5 text-viridia" data-id="element-1810" data-mp="3sp1m7g46le3l" />
                  </div>
                  <div data-id="element-1811" data-mp="1iqg35kbdn5e">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1812" data-mp="1i8jg7tqp6wy">
                      Complex Approval Processes
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1813" data-mp="t8mttu2jo13v">
                      Quotes over a certain amount need manager approval, or
                      specific job types need to be routed to specific
                      technicians based on logic.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1814" data-mp="18545kmenbhqy">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1815" data-mp="184axni3yam78">
                    <DatabaseIcon className="w-5 h-5 text-viridia" data-id="element-1816" data-mp="1mtdwi4zx0xqz" />
                  </div>
                  <div data-id="element-1817" data-mp="1hu11uy2symdu">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1818" data-mp="1huswa5knye2j">
                      Heavy Admin Burden
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1819" data-mp="1cwrdzmv2ais4">
                      Your team spends hours every week copy-pasting information
                      from emails into spreadsheets or generating manual
                      reports.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1820" data-mp="u3dnbtydsyib">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1821" data-mp="u22mo308g6vf">
                <h3 className="text-xl font-bold text-white mb-6 text-center" data-id="element-1822" data-mp="p6x3hby7t6b1">
                  The Cost of Manual Work
                </h3>
                <div className="space-y-6" data-id="element-1823" data-mp="g4nu4peoniz8">
                  <div data-id="element-1824" data-mp="g4xkwf7jxu5z">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1825" data-mp="g6idhzab3317">
                      <span className="text-gray-400" data-id="element-1826" data-mp="2rr7swexxpfad">Manual Data Entry</span>
                      <span className="text-red-400" data-id="element-1827" data-mp="2vsb7yz85r7aj">High Error Rate</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1828" data-mp="30th2n2ldp4mp">
                      <div className="bg-red-500 h-2 rounded-full" style={{
                      width: '85%'
                    }} data-id="element-1829" data-mp="35ra6hoz7lmjt"></div>
                    </div>
                  </div>
                  <div data-id="element-1830" data-mp="2d0s8h2dcebd9">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1831" data-mp="2i27sqs7p4dul">
                      <span className="text-gray-400" data-id="element-1832" data-mp="2hzv11nc4yp4c">Information Silos</span>
                      <span className="text-yellow-400" data-id="element-1833" data-mp="2mu8xzu6g54b4">Slow Handoffs</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1834" data-mp="1pcr13wrwh0zf">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-1835" data-mp="1ubbsd6p6cabq"></div>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-800" data-id="element-1836" data-mp="9lojyf5enu0v">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1837" data-mp="9o18jb5o52mw">
                      <span className="text-white font-medium" data-id="element-1838" data-mp="4uga5i05y6n6">
                        Automated System
                      </span>
                      <span className="text-viridia font-medium" data-id="element-1839" data-mp="jlyvzwfpeveg">
                        Instant & Accurate
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1840" data-mp="sjj1xioihfve">
                      <div className="bg-viridia h-2 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1841" data-mp="nlhgir3m54gr"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-viridia-surface" data-id="element-1842" data-mp="19t3c4plen7n4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1843" data-mp="19vzeqr876khw">
          <ScrollReveal data-id="element-1844" data-mp="w28bu1oz1yv5">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1845" data-mp="w0x8u43cov53">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1846" data-mp="102s9d6dgj4ht">
                Examples of Custom Workflows
              </h2>
              <p className="text-gray-400" data-id="element-1847" data-mp="m6wwbh5qonmc">
                If it has an API, we can connect it. Here are a few examples of
                systems we can build.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1848" data-mp="h57hrrjb5rti">
            {[{
            title: 'Intelligent Lead Routing',
            desc: 'Web form submissions are analyzed by AI. High-value commercial leads are texted directly to the owner, while residential leads go to the standard sales queue.',
            icon: GitMergeIcon
          }, {
            title: 'Cross-Platform Syncing',
            desc: 'When a job is marked "Complete" in Jobber, automatically generate an invoice in QuickBooks and send a review request via Viridia CRM.',
            icon: WorkflowIcon
          }, {
            title: 'AI Call Summaries to Slack',
            desc: "After every recorded sales call, AI generates a 3-bullet summary of the customer's needs and posts it to a Slack channel for the fulfillment team.",
            icon: MessageSquareIcon
          }, {
            title: 'Automated Reporting',
            desc: 'Pull data from Google Ads, Facebook Ads, and your CRM into a custom Google Sheet that updates daily without manual export/import.',
            icon: FileTextIcon
          }, {
            title: 'Inventory Alerts',
            desc: 'When a specific service is booked, automatically check inventory levels in your management software and alert the purchasing manager if parts are needed.',
            icon: AlertCircleIcon
          }, {
            title: 'Custom Webhooks',
            desc: 'Need your proprietary internal software to trigger actions in modern marketing tools? We build the middleware to make it happen.',
            icon: SettingsIcon
          }].map((example, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1849" data-mp="1h6lslwn6pzvh" data-mpi={index}>
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full hover:border-viridia/30 transition-colors" data-id="element-1850" data-mp="1lyvff3t06jtj">
                  <div className="w-10 h-10 rounded bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4" data-id="element-1851" data-mp="1qwesi0dysqjw">
                    <example.icon className="w-5 h-5 text-viridia" data-id="element-1852" data-mp="1uxgsijfph6mx" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1853" data-mp="1zwblufbaudla">
                    {example.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1854" data-mp="2gaibhsh5h6l3">
                    {example.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1855" data-mp="2q6cz7208druq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1856" data-mp="22jmwdyg25t92">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1857" data-mp="1xkacnmkhh2ap">
            <ScrollReveal data-id="element-1858" data-mp="1xhxnaocebpnm">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1859" data-mp="2c96kpjmjifbb">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1860" data-mp="27gwx4a8wdrc7">
                Our custom automation services are designed for businesses with
                established operations that need to scale without adding more
                administrative headcount.
              </p>
              <ul className="space-y-4" data-id="element-1861" data-mp="3qg49bplznfu1">
                <li className="flex items-start gap-3" data-id="element-1862" data-mp="3qfbbje50c0o1">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1863" data-mp="3v7ulm14hkdo3" />
                  <div data-id="element-1864" data-mp="3giq64rp2snjo">
                    <h4 className="font-bold text-white" data-id="element-1865" data-mp="3gjhr78l1r2vp">
                      Growing Contractors
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1866" data-mp="3li2u5froo0nu">
                      HVAC, painters, landscapers, window installers, and other
                      contractors that have outgrown manual processes and need
                      their software stack to work together seamlessly.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1867" data-mp="823obrek1erv">
                  <UsersIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1868" data-mp="84pvvr1g3ua3" />
                  <div data-id="element-1869" data-mp="2opz60649uep5">
                    <h4 className="font-bold text-white" data-id="element-1870" data-mp="2otn0b7hjj54x">
                      Teams with Admin Bottlenecks
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1871" data-mp="1rtwq4nk9ux98">
                      If your office staff spends hours copying data between
                      systems, you need custom automation.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-1872" data-mp="25t4tztrdqtjr">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1873" data-mp="25s3dvavibgv6">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1874" data-mp="194goty0rj0rr">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-1875" data-mp="1d1utrdheumdy">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-black rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1876" data-mp="1i295b5i80egq">
                    <div className="font-bold text-white mb-1" data-id="element-1877" data-mp="pe3hg9vve9co">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1878" data-mp="ubd8ftywqt2m">
                      Our flagship system for instant lead response and booking.
                    </div>
                  </Link>
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-black rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1879" data-mp="htasytdg25nf">
                    <div className="font-bold text-white mb-1" data-id="element-1880" data-mp="3v2qy71jwe5e">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1881" data-mp="3v98fu5eyyvgz">
                      Centralize your operations with our robust CRM platform.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1882" data-mp="3h9fqoej3cnb4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1883" data-mp="3ccgzo4uq4wh5">
          <ScrollReveal data-id="element-1884" data-mp="32mh4axz3cj39">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1885" data-mp="32lz1tjlu58mw">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1886" data-mp="2spcg6sloqdjt">
              Everything you need to know about our custom automation services.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1887" data-mp="2xqjp4uzkdis2">
          <ScrollReveal delay={0.2} data-id="element-1888" data-mp="3lfclgsk3lsn7">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1889" data-mp="3lgdz94si0t8m">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1890" data-mp="3bp05zer54pnx" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1891" data-mp="85osnvchr4h3">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1892" data-mp="czj6wj9bur8s">
          <ScrollReveal data-id="element-1893" data-mp="1xnc4wqfgaxtt">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1894" data-mp="1xmiw7jwxly91">
              Let's Map Out Your System
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-1895" data-mp="26gp6egrkz7ri">
              Book a technical scoping call. We'll review your current software
              stack and identify exactly where automation can save you time and
              money.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1896" data-mp="1eq17lbddqeh0">
              Book a Scoping Call
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}